import {toggleToast} from '@github-ui/toggle-toast'
import {controller, target, targets} from '@github/catalyst'

// This element is responsible for serializing and persisting
// search options for users who don't like the default options for the Forks search parameters.
// Only items that need to be serialized need to be bound here.
// The only element that requires this javascript to function is the saveOptionsButton element.
// Very contrived example:
//
//  <search-control-menu>
//     <h2>Sort By</h2>
//     <button data-target="search-control-menu.selectedSortOption" data-value="florps">Florps!</button>
//     <button>Blorps!</button>
//     <!-- etc. -->
//     <button data-target="search-control-menu.saveOptionsButton"
//             data-action="click:search-control-menu#saveDefaultOptions"
//             data-href="/repo/owner/forks/save-default-options">Save!</button>
//  </search-control-menu>
@controller('search-control-menu')
class SearchControlMenuElement extends HTMLElement {
  allowSubmit = true
  declare buttonEnabledMessage: string

  @target declare selectedPeriodOption: HTMLElement
  @target declare selectedSortOption: HTMLElement
  @targets declare selectedRepositoryTypeOptions: HTMLElement[]
  @target declare saveOptionsButton: HTMLElement
  @target declare authenticityTokenInput: HTMLInputElement
  @target declare successToast: HTMLElement
  @target declare errorToast: HTMLElement

  // This is the onClick binding for the saveOptionsButton element.
  async saveDefaultOptions() {
    if (this.allowSubmit) {
      this.submitPolitely()
    }
  }

  // Serializes the currently selected elements into a payload for
  // for the controller. Invalid values here will be silently overridden by
  // application defaults on the controller side.
  buildFormData(): FormData {
    const formData = new FormData()
    const selectedRepositoryTypes: string = this.selectedRepositoryTypeOptions
      .map(option => {
        return option.getAttribute('data-value')!
      })
      .join(',')
    formData.append('period', this.selectedPeriodOption.getAttribute('data-value')!)
    formData.append('sort_by', this.selectedSortOption.getAttribute('data-value')!)
    formData.append('include', selectedRepositoryTypes)
    return formData
  }

  // This attempts to preserve the button width after the text changes,
  // however if the new text is longer, this appears to have no effect.
  // At the very least, this prevents a wacky jitter in and out as the
  // text changes, only expanding the button if needed, and never
  // shrinking it.
  restoreSubmitButtonWidth(width: number) {
    this.saveOptionsButton.style.width = `${width}px`
  }

  // This is a reimplementation of the `data-disable-with` behavior because
  // the default behavior only works on `type=submit` buttons, and *those*
  // require inclusion inside a form tag, and *that* caused an error that was
  // just one rabbit hole too far. Another difference is that the message
  // is not binary; we have three states represented:
  // enabled, disabled while saving, disabled after saving.
  disableSubmit(message?: string) {
    const currentWidth = this.saveOptionsButton.getBoundingClientRect().width
    if (typeof message === 'undefined') {
      message = this.saveOptionsButton.getAttribute('data-disable-with')!
    }

    this.allowSubmit = false
    this.saveOptionsButton.setAttribute('disabled', 'disabled')
    this.saveOptionsButton.textContent = message!
    this.restoreSubmitButtonWidth(currentWidth)
  }

  enableSubmit() {
    const currentWidth = this.saveOptionsButton.getBoundingClientRect().width
    this.allowSubmit = true
    this.saveOptionsButton.removeAttribute('disabled')
    this.saveOptionsButton.textContent = this.buttonEnabledMessage!
    this.restoreSubmitButtonWidth(currentWidth)
  }

  // Submits the payload and toasts the user to let them know whether
  // the option was a success or a failure. For a single page view,
  // this will only succeed once, thereafter disabling the button until
  // the options change.
  async submitPolitely() {
    this.buttonEnabledMessage = this.saveOptionsButton.textContent!
    this.disableSubmit('Saving...')
    try {
      const response = await fetch(this.saveOptionsButton.getAttribute('data-href')!, {
        method: 'POST',
        body: this.buildFormData(),
        headers: {
          Accept: 'application/json',
          mode: 'same-origin',
          'Scoped-CSRF-Token': this.authenticityTokenInput.value,
        },
      })
      if (response.status !== 200) {
        throw new Error('Error saving default options.')
      }
      this.disableSubmit()
      toggleToast(this.successToast.innerHTML)
    } catch {
      toggleToast(this.errorToast.innerHTML)
      this.enableSubmit()
    }
  }
}
